import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { Footer } from '@/components/layout/Footer';
import { OpenGraphTags, ShareDialog } from '@/components/sharing';
import { Home } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useShareAnalytics } from '@/hooks/use-share-analytics';
import { supabase } from '@/integrations/supabase/client';
import { ToolHeader, ToolPreview, CommentsSection, ToolDetails } from '@/components/tools';
import { Tool, ToolComment, ToolInteractions } from '@/types/tool';
import { useTranslation } from 'react-i18next';
import { pickI18nString, pickI18nValue } from '@/lib/i18nUtils';
import { DEFAULT_LANG } from '@/i18n';
import { createToolQuery, isUUID } from '@/lib/slugUtils';

const ToolDetail: React.FC = () => {
  const { toolId: toolParam } = useParams<{ toolId: string }>();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { trackShare } = useShareAnalytics();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const [tool, setTool] = useState<Tool | null>(null);
  const [comments, setComments] = useState<ToolComment[]>([]);
  const [interactions, setInteractions] = useState<ToolInteractions>({
    isBookmarked: false,
    hasVoted: false,
    userVoteType: null
  });
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!toolParam) return;
      
      setLoading(true);
      await fetchToolData();
      setLoading(false);
    };
    
    loadData();
  }, [toolParam]);

  useEffect(() => {
    if (tool) {
      fetchComments();
      if (user) {
        fetchUserInteractions();
      }
    }
  }, [tool, user]);

  const fetchToolData = async () => {
    try {
      const isId = isUUID(toolParam!);
      let query = supabase
        .from('tools')
        .select(`
          id,
          slug,
          name,
          name_i18n,
          description,
          description_i18n,
          details_i18n,
          icon,
          is_featured,
          upvotes_count,
          created_at,
          category:categories(name, name_i18n),
          tags:tool_tags(tags(name, name_i18n, color)),
          tool_type:tool_types(name)
        `)
        .eq('is_active', true);
        
      if (isId) {
        query = query.eq('id', toolParam!);
      } else {
        query = query.eq('slug', toolParam!);
      }
      
      const { data, error } = await query.single();

      if (error) throw error;

      const langCode = i18n.language || DEFAULT_LANG;
      const localizedName = pickI18nString((data as any).name_i18n, (data as any).name, langCode, DEFAULT_LANG);
      const localizedDesc = pickI18nString((data as any).description_i18n, (data as any).description, langCode, DEFAULT_LANG);
      const localizedCategory = (data as any).category
        ? pickI18nString((data as any).category.name_i18n, (data as any).category.name, langCode, DEFAULT_LANG)
        : undefined;
      const details = pickI18nValue<any>((data as any).details_i18n, langCode, DEFAULT_LANG);

      const transformed: Tool = {
        ...(data as any),
        name: localizedName,
        description: localizedDesc,
        category: localizedCategory ? { name: localizedCategory } : (data as any).category,
        details,
      };

      setTool(transformed);
    } catch (error) {
      console.error('Error fetching tool:', error);
      toast({
        title: "Error",
        description: "Failed to load tool details.",
        variant: "destructive",
      });
    }
  };

  const fetchComments = async () => {
    if (!tool?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('tool_comments')
        .select(`
          id,
          comment_text,
          created_at,
          user_id,
          profiles:user_id(display_name, avatar_url)
        `)
        .eq('tool_id', tool.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchUserInteractions = async () => {
    if (!user || !tool?.id) return;

    try {
      // Check if bookmarked
      const { data: bookmarkData } = await supabase
        .from('tool_bookmarks')
        .select('*')
        .eq('tool_id', tool.id)
        .eq('user_id', user.id)
        .single();

      // Check if voted
      const { data: voteData } = await supabase
        .from('tool_votes')
        .select('vote_type')
        .eq('tool_id', tool.id)
        .eq('user_id', user.id)
        .single();

      setInteractions({
        isBookmarked: !!bookmarkData,
        hasVoted: !!voteData,
        userVoteType: voteData?.vote_type || null
      });
    } catch (error) {
      console.error('Error fetching user interactions:', error);
    }
  };

  const handleVote = async (voteType: 'upvote' | 'downvote') => {
    if (!user || !tool?.id) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to vote on tools.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (interactions.hasVoted && interactions.userVoteType === voteType) {
        // Remove vote
        const { error } = await supabase
          .from('tool_votes')
          .delete()
          .eq('tool_id', tool.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setInteractions(prev => ({
          ...prev,
          hasVoted: false,
          userVoteType: null
        }));
      } else {
        // Add or update vote
        const { error } = await supabase
          .from('tool_votes')
          .upsert({
            tool_id: tool.id,
            user_id: user.id,
            vote_type: voteType
          });

        if (error) throw error;

        setInteractions(prev => ({
          ...prev,
          hasVoted: true,
          userVoteType: voteType
        }));
      }

      // Refresh tool data to get updated vote count
      fetchToolData();
    } catch (error) {
      console.error('Error voting:', error);
      toast({
        title: "Error",
        description: "Failed to submit vote.",
        variant: "destructive",
      });
    }
  };

  const handleBookmark = async () => {
    if (!user || !tool?.id) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to bookmark tools.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (interactions.isBookmarked) {
        // Remove bookmark
        const { error } = await supabase
          .from('tool_bookmarks')
          .delete()
          .eq('tool_id', tool.id)
          .eq('user_id', user.id);

        if (error) throw error;
        
        setInteractions(prev => ({ ...prev, isBookmarked: false }));
        toast({
          title: "Bookmark Removed",
          description: "Tool removed from your bookmarks.",
        });
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('tool_bookmarks')
          .insert({
            tool_id: tool.id,
            user_id: user.id
          });

        if (error) throw error;
        
        setInteractions(prev => ({ ...prev, isBookmarked: true }));
        toast({
          title: "Bookmark Added",
          description: "Tool added to your bookmarks.",
        });
      }
    } catch (error) {
      console.error('Error bookmarking:', error);
      toast({
        title: "Error",
        description: "Failed to update bookmark.",
        variant: "destructive",
      });
    }
  };

  const handleSubmitComment = async () => {
    if (!user || !tool?.id) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to comment on tools.",
        variant: "destructive",
      });
      return;
    }

    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const { error } = await supabase
        .from('tool_comments')
        .insert({
          tool_id: tool.id,
          user_id: user.id,
          comment_text: newComment.trim()
        });

      if (error) throw error;

      setNewComment('');
      fetchComments();
      toast({
        title: "Comment Added",
        description: "Your comment has been posted successfully.",
      });
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: "Error",
        description: "Failed to post comment.",
        variant: "destructive",
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = () => {
    setShowShareDialog(true);
    // Track share dialog open
    if (tool) {
      trackShare(tool.id, 'dialog_open', user?.id);
    }
  };

  const handleNavigateToConfig = () => {
    navigate(`${prefix}/tools/${tool?.slug || tool?.id}/configuration`);
  };

  const handleEditComment = async (commentId: string, newText: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('tool_comments')
        .update({ comment_text: newText.trim() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      fetchComments();
      toast({
        title: "Comment Updated",
        description: "Your comment has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      toast({
        title: "Error",
        description: "Failed to update comment.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('tool_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      fetchComments();
      toast({
        title: "Comment Deleted",
        description: "Your comment has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: "Error",
        description: "Failed to delete comment.",
        variant: "destructive",
      });
    }
  };

  const handleNavigateToAuth = () => {
    navigate(`${prefix}/auth`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin mx-auto w-8 h-8 border-2 border-primary border-t-transparent rounded-full mb-4"></div>
          <p className="text-muted-foreground">{t('tool.detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('tool.detail.notFound')}</h1>
          <p className="text-muted-foreground mb-6">{t('tool.detail.notFoundDesc')}</p>
          <Button onClick={() => navigate(prefix)}>
            <Home className="w-4 h-4 mr-2" />
            {t('tool.detail.backToHub')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      {/* SEO Meta Tags for Social Sharing */}
      {tool && (
        <OpenGraphTags
          title={`${tool.name} - JiGSO EX Hub`}
          description={tool.description}
          url={`${window.location.origin}${prefix}/tools/${tool.slug || tool.id}`}
          category={tool.category?.name}
          upvotes={tool.upvotes_count}
        />
      )}

      <ToolHeader
        tool={tool}
        interactions={interactions}
        prefix={prefix}
        onVote={handleVote}
        onBookmark={handleBookmark}
        onShare={handleShare}
        onNavigateToConfig={handleNavigateToConfig}
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <ToolPreview
            toolId={tool.id}
            prefix={prefix}
            onNavigateToConfig={handleNavigateToConfig}
            toolDetails={tool.details}
            toolType={tool.tool_type?.name}
          />

          {/* {tool.details && (
            <ToolDetails details={tool.details} />
          )} */}

          <CommentsSection
            comments={comments}
            newComment={newComment}
            submittingComment={submittingComment}
            user={user}
            prefix={prefix}
            onCommentChange={setNewComment}
            onSubmitComment={handleSubmitComment}
            onNavigateToAuth={handleNavigateToAuth}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
          />
        </div>
      </div>

      {/* Share Dialog */}
      <ShareDialog 
        tool={tool} 
        isOpen={showShareDialog} 
        onClose={() => setShowShareDialog(false)} 
        onTrackShare={(platform) => trackShare(tool.id, platform, user?.id)}
      />
      
      <Footer />
    </AppLayout>
  );
};

export default ToolDetail;